import { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

import MiniChart from '@/assets/img/MiniChart.png'
import MiniChartUp from '@/assets/img/MiniChartUp.png'
import {
  walletSelector,
  walletTransactionsSelector,
} from '@/core/services/Wallets/WalletSelector'
import {
  getWalletOperationsThunk,
  getWalletThunk,
} from '@/core/services/Wallets/WalletSlice'
import { useAppDispatch, useAppSelector } from '@/core/store/hooks'
import { cn } from '@bem-react/classname'

import { useHandleServerError } from '@/hooks/useHandleServerError'

import { Back } from '@/components/UI/Back'
import Icon from '@/components/UI/Icon'
import { Loading } from '@/components/UI/Loading'
import { Title } from '@/components/UI/Title'

import { WalletChart } from './components/WalletChart'
import { WalletOperations } from './components/WalletOperations'
import './styles/Wallet.scss'

const cnWallet = cn('Wallet')

export const Wallet: FC = () => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const { code } = useParams<{ code: string }>()
  const { handleServerError } = useHandleServerError()

  const wallet = useAppSelector(walletSelector)
  const transactions = useAppSelector(walletTransactionsSelector)

  const [isLoading, setLoading] = useState<boolean>(true)

  useEffect(() => {
    if (code) {
      getWallet(code)
      getWalletOperations(code)
    }
  }, [code])

  const getWallet = async (code: string) => {
    try {
      await dispatch(getWalletThunk(code)).unwrap()
      setLoading(false)
    } catch (error) {
      handleServerError(error)
      setLoading(false)
    }
  }

  const getWalletOperations = async (code: string, page = 1) => {
    try {
      await dispatch(
        getWalletOperationsThunk({ code, page: page + '' }),
      ).unwrap()
      setLoading(false)
    } catch (error) {
      handleServerError(error)
      setLoading(false)
    }
  }

  const up = true

  return (
    <>
      {isLoading ? (
        <Loading type='circle' minHeight='300px' />
      ) : (
        <div className={cnWallet()}>
          {wallet && (
            <>
              <div className={cnWallet('top')}>
                <Back />

                <div className={cnWallet('left')}>
                  <div
                    className={cnWallet(
                      'icon',
                      `icon-${wallet.code} ${wallet.code}-color-bg`,
                    )}
                  ></div>
                  <div className={cnWallet('wrap')}>
                    <div className={cnWallet('code')}>{wallet.code}</div>
                    {/* <div className={cnWallet('name')}>{wallet.name}</div> */}
                  </div>
                </div>

                <div className={cnWallet('center')}>
                  <div className={cnWallet('graph')}>
                    {up ? (
                      <img src={MiniChartUp} alt='MiniChartUp' />
                    ) : (
                      <img src={MiniChart} alt='MiniChart' />
                    )}
                  </div>
                  <div className={cnWallet('info')}>
                    <div className={cnWallet('value')}>
                      {wallet.balance} {wallet.code}
                    </div>
                    {/* <div className={cnWallet('small', { up })}>
                      {up ? (
                        <svg
                          xmlns='http://www.w3.org/2000/svg'
                          width='6'
                          height='15'
                          viewBox='0 0 6 15'
                          fill='none'
                        >
                          <path
                            d='M0.6 8.7L2.6 6.03333C2.8 5.76667 3.2 5.76667 3.4 6.03333L5.4 8.7C5.64721 9.02962 5.41202 9.5 5 9.5H1C0.587977 9.5 0.352786 9.02962 0.6 8.7Z'
                            fill='#30DAAD'
                          />
                        </svg>
                      ) : (
                        <svg
                          xmlns='http://www.w3.org/2000/svg'
                          width='6'
                          height='15'
                          viewBox='0 0 6 15'
                          fill='none'
                        >
                          <g clipPath='url(#clip0_2059_814)'>
                            <path
                              d='M5.4 6.3L3.4 8.96667C3.2 9.23333 2.8 9.23333 2.6 8.96667L0.6 6.3C0.352787 5.97038 0.587978 5.5 1 5.5L5 5.5C5.41202 5.5 5.64721 5.97038 5.4 6.3Z'
                              fill='#FF6A38'
                            />
                          </g>
                          <defs>
                            <clipPath id='clip0_2059_814'>
                              <rect
                                width='6'
                                height='14'
                                fill='white'
                                transform='translate(0 0.5)'
                              />
                            </clipPath>
                          </defs>
                        </svg>
                      )}

                      <span>{trimTrailingZeros(wallet.course)}</span>
                      {' · '}
                      <span>0.64%</span>
                    </div> */}
                  </div>
                </div>

                <div className={cnWallet('actions')}>
                  <button
                    type='button'
                    className={cnWallet('action')}
                    onClick={e => {
                      e.preventDefault()
                      e.stopPropagation()
                      navigate(`/c/wallets/${code}/receive`)
                    }}
                  >
                    <Icon name='receive' />
                    Receive
                  </button>
                  <button
                    className={cnWallet('action')}
                    onClick={e => {
                      e.preventDefault()
                      e.stopPropagation()
                      navigate(`/c/wallets/${code}/send`)
                    }}
                  >
                    <Icon name='send' />
                    Send
                  </button>
                  <button className={cnWallet('action', { buy: true })}>
                    <Icon name='buy' />
                    Buy
                  </button>
                </div>
              </div>

              <WalletChart
                className={cnWallet('chat')}
                code={code?.toLowerCase() || ''}
              />

              <Title>
                {t('wallet-title', { code: wallet.code.toUpperCase() })}
              </Title>

              {transactions && (
                <WalletOperations
                  code={wallet.code}
                  transactions={transactions}
                />
              )}
            </>
          )}
        </div>
      )}
    </>
  )
}
